import { Box, Typography } from "@mui/material";
import Head from "next/head";
import { useEffect } from "react";
import Breadcrumbs from "../src/components/common/Breadcrumbs";
import { MemoizedFooter } from "../src/layouts/footer";
import { MemoizedHeader } from "../src/layouts/header";
import { sendAnalytics } from "../src/utils/analyticsCommon";

const contentSx = {
  maxWidth: 900,
  margin: "0 auto",
  "& h1": {
    fontSize: { xs: "1.75rem", md: "2rem" },
    fontWeight: 700,
    marginBottom: 2,
    marginTop: 0,
  },
  "& h2": {
    fontSize: { xs: "1.25rem", md: "1.375rem" },
    fontWeight: 700,
    marginTop: 5,
    marginBottom: 1.5,
    lineHeight: 1.3,
  },
  "& h2:first-of-type": { marginTop: 3 },
  "& h3": {
    fontSize: { xs: "1.1rem", md: "1.125rem" },
    fontWeight: 600,
    marginTop: 3,
    marginBottom: 1,
  },
  "& p": {
    marginBottom: 1.5,
    lineHeight: 1.7,
    textAlign: "left",
    overflowWrap: "anywhere",
  },
  "& ul, & ol": {
    marginBottom: 1.5,
    paddingLeft: { xs: 2.5, md: 3 },
    "& li": { marginBottom: 0.5, lineHeight: 1.6 },
  },
  "& table": {
    width: "100%",
    borderCollapse: "collapse",
    marginBottom: 2,
    fontSize: { xs: "0.95rem", md: "1rem" },
  },
  "& th, & td": {
    border: "1px solid #D9D9D9",
    padding: { xs: "8px 10px", md: "10px 12px" },
    verticalAlign: "top",
    textAlign: "left",
    overflowWrap: "anywhere",
  },
  "& th": {
    backgroundColor: "#F6F6F6",
    fontWeight: 700,
  },
  "& a": {
    color: "primary.main",
    textDecoration: "underline",
    overflowWrap: "anywhere",
  },
};

const BREADCRUMB_ITEMS = [
  { label: "Home", href: "/" },
  { label: "Grievance Redressal Policy" },
];

const BREADCRUMB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: "https://www.turno.club/",
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "Grievance Redressal Policy",
      item: "https://www.turno.club/grievance-redressal-policy",
    },
  ],
};

export default function GrievanceRedressalPolicy() {
  useEffect(() => {
    sendAnalytics(
      "screen_viewed",
      { screen_name: "grievance-redressal-policy" },
      "en"
    );
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <Head>
        <title>Grievance Redressal Policy</title>
        <meta
          name="description"
          content="Customer Grievance Redressal Mechanism and consent withdrawal process for Blubble Private Limited (Turno)."
        />
        <meta
          name="keywords"
          content="Turno, Grievance Redressal Policy, Customer Grievance Redressal Mechanism, Blubble Private Limited, consent withdrawal, complaint process"
        />
        <meta name="author" content="Turno" />
        <meta
          name="url"
          content="https://www.turno.club/grievance-redressal-policy"
        />
        <meta name="robots" content="INDEX,ARCHIVE,FOLLOW" />
        <meta name="lang" content="en" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link
          rel="canonical"
          href="https://www.turno.club/grievance-redressal-policy"
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(BREADCRUMB_SCHEMA),
          }}
          defer
        />
      </Head>

      <MemoizedHeader />

      <Box
        sx={{
          padding: {
            xs: "16px",
            sm: "20px",
            md: "30px 50px 40px",
            lg: "30px 80px 40px",
          },
        }}
      >
        <Box component="article" sx={contentSx}>
          <Typography component="h1" variant="h4">
            Customer Grievance Redressal Mechanism
          </Typography>

          <p>
            <strong>Company:</strong> Blubble Private Limited
          </p>
          <p>
            <strong>Phone:</strong> +91-8861012323
          </p>
          <p>
            <strong>Email:</strong>{" "}
            <a href="mailto:info@turno.club">info@turno.club</a>
          </p>
          <p>
            <strong>Website:</strong>{" "}
            <a
              href="https://www.turno.club"
              target="_blank"
              rel="noopener noreferrer"
            >
              www.turno.club
            </a>
          </p>
          <p>
            <strong>Address:</strong> Industrial Plot Bearing No 7-A1, 3rd cross
            road, Devasandra II phase industrial area, KR Puram Hobli, Bangalore
            - 560048
          </p>

          <Typography component="h2">1. Objective</Typography>
          <p>
            Blubble Private Limited (&quot;Company&quot;) is committed to
            maintaining a transparent, fair, and efficient grievance redressal
            mechanism for its employees, customers, vendors, and stakeholders.
            This policy ensures a structured process for resolving customer
            complaints related to financing, credit reporting, and other
            services provided by Blubble private Ltd. This mechanism is
            board-approved and reviewed periodically as per the Company&apos;s
            review policy.
          </p>

          <Typography component="h2">2. Scope &amp; Applicability</Typography>
          <p>
            This grievance redressal mechanism applies to all customer
            complaints, including but not limited to:
          </p>
          <ul>
            <li>Loan disbursement and repayment issues</li>
            <li>Credit Report discrepancies as per CIC Regulations, 2006</li>
            <li>
              Consent-based sharing of Credit Information under CICRA, 2005
            </li>
          </ul>

          <Typography component="h2">
            3. Grievance Redressal Process
          </Typography>
          <Typography component="h3">a) Submission of Grievance</Typography>
          <p>Complaints can be submitted through the following channels:</p>
          <ul>
            <li>
              Email: <a href="mailto:care@turno.club">care@turno.club</a>
            </li>
            <li>
              Website:{" "}
              <a
                href="https://www.turno.club"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://www.turno.club
              </a>
            </li>
            <li>Customer Helpline: +91-8861012323</li>
            <li>
              Head Office Address: 7-A1, 3rd Cross Rd, near VR mall,
              Singayyanapalya, Krishnarajapuram, Bengaluru, Karnataka 560048
            </li>
            <li>
              Anonymous complaints may be considered if sufficient details are
              provided.
            </li>
          </ul>

          <Typography component="h3">
            b) Acknowledgment &amp; Resolution Timeline
          </Typography>
          <ul>
            <li>Complaints will be acknowledged within 3-5 working days.</li>
            <li>
              The investigation will be completed, and a resolution will be
              provided within 30 working days.
            </li>
            <li>
              If additional time is required, an interim response will be
              communicated with an expected resolution timeline.
            </li>
          </ul>

          <Typography component="h2">4. Consent Withdrawal Process</Typography>
          <p>
            Customers who have provided consent for sharing their Credit
            Information and wish to withdraw it can do so via:
          </p>
          <ul>
            <li>
              Email: <a href="mailto:care@turno.club">care@turno.club</a>
            </li>
            <li>Online Form: Available on the Company&apos;s website</li>
            <li>Written Request: Sent to the Grievance Officer</li>
          </ul>
          <p>Upon receipt of a valid request:</p>
          <ul>
            <li>
              The Company will delete stored Credit Information and confirm the
              action within 7 working days.
            </li>
            <li>
              If the request is not actioned, it can be escalated as per the
              Escalation Matrix below.
            </li>
          </ul>

          <Typography component="h2">5. Escalation Matrix</Typography>
          <p>
            If the grievance is not resolved within the stipulated time, it may
            be escalated as follows:
          </p>
          <Box sx={{ width: "100%", overflowX: "auto", marginBottom: 2 }}>
            <Box component="table" sx={{ minWidth: { xs: 720, md: "100%" } }}>
              <thead>
                <tr>
                  <th>Sl.</th>
                  <th>Level</th>
                  <th>Response Time</th>
                  <th>Turn-around time (Working Days)</th>
                  <th>Name and Designation</th>
                  <th>Contact Details</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>1</td>
                  <td>Grievance Officer</td>
                  <td>72 Hours</td>
                  <td>7</td>
                  <td>Ashikraj</td>
                  <td>
                    Email:{" "}
                    <a href="mailto:ashik.raj@turno.club">
                      ashik.raj@turno.club
                    </a>
                  </td>
                </tr>
                <tr>
                  <td>2</td>
                  <td>Senior Management</td>
                  <td>48 Hours</td>
                  <td>10</td>
                  <td>Varun Das</td>
                  <td>
                    Email:{" "}
                    <a href="mailto:varun.das@turno.club">
                      varun.das@turno.club
                    </a>
                  </td>
                </tr>
              </tbody>
            </Box>
          </Box>
          <p>
            Escalation matrix remains same for both Consent Withdrawal Process
            and Grievance Redressal Policy.
          </p>
          <p>
            If any complaint lodged by the borrower against the RE or the LSP
            engaged by the RE is rejected wholly or partly by the RE, or the
            borrower is not satisfied with the reply, or the borrower has not
            received any reply within 30 days of receipt of complaint by the
            RE, the said borrower can lodge a complaint over the Complaint
            Management System (CMS) portal under the Reserve Bank-Integrated
            Ombudsman Scheme (RB-IOS) or send a physical complaint to
            &quot;Centralised Receipt and Processing Centre, 4th Floor,
            Reserve Bank of India, Sector-17, Central Vista, Chandigarh -
            160017&quot; as per the grievance redressal mechanism prescribed by
            the Reserve Bank. This information shall be suitably conveyed to
            the borrower.
          </p>

          <Typography component="h2">8. Board Oversight &amp; Compliance</Typography>
          <ul>
            <li>
              The Board of Directors shall periodically review the grievance
              redressal mechanism.
            </li>
            <li>
              A consolidated review report on grievance redressal shall be
              submitted to the Board at regular intervals.
            </li>
          </ul>

          <Typography component="h2">
            7. Fair Practices &amp; Customer Protection
          </Typography>
          <ul>
            <li>
              The Company shall ensure that customer data is handled securely
              and not misused by third parties or outsourced service providers.
            </li>
            <li>
              Interest rate policies, loan terms, and recovery practices shall
              be disclosed transparently to customers to prevent grievances
              related to excessive charges.
            </li>
          </ul>

          <Typography component="h2">
            9. Non-Retaliation &amp; Compliance
          </Typography>
          <ul>
            <li>
              The Company strictly prohibits retaliation against any person
              filing a grievance or consent withdrawal request in good faith.
            </li>
            <li>
              All logs related to Consent Capture and Withdrawal will be
              permanently maintained for verification.
            </li>
          </ul>

          <Typography component="h2">10. Adoption &amp; Review</Typography>
          <ul>
            <li>
              This policy shall be reviewed periodically to ensure its
              effectiveness and compliance with regulatory requirements.
            </li>
            <li>
              Any amendments will be approved by the Board of Directors and
              communicated accordingly.
            </li>
          </ul>

          <Typography component="h2">Contact for Grievances</Typography>
          <p>
            For any grievances, please contact: Grievance Officer, Turno Private
            Limited
          </p>
          <p>
            <strong>Email:</strong>{" "}
            <a href="mailto:care@turno.club">care@turno.club</a>
          </p>
          <p>
            <strong>Phone:</strong> 8861012323
          </p>
          <p>
            <strong>For CIBIL Credit Information Report:</strong> TransUnion
            CIBIL{" "}
            <a
              href="https://www.cibil.com/contact-us-faq"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://www.cibil.com/contact-us-faq
            </a>
          </p>
        </Box>

        <Box
          sx={{
            width: { xs: "100%", md: "84%" },
            margin: "20px auto",
            padding: { xs: "0 12px", md: "0" },
          }}
        >
          <Breadcrumbs items={BREADCRUMB_ITEMS} />
        </Box>
      </Box>

      <MemoizedFooter />
    </>
  );
}
